<div class="max-w-4xl mx-auto p-8 min-h-screen">
  <p-toast></p-toast>
  
  <!-- Header Section -->
  <div class="text-center mb-8">
    <h2 class="font-semibold mb-2" i18n>Setup Your Account</h2>
    <p class="text-gray-600 text-lg m-0" i18n>Fill in the information below</p>
  </div>

  <!-- Progress Section -->
  <div class="mb-8">
    <div class="flex items-center justify-between mb-4">
      <span class="text-sm font-medium text-gray-500" i18n >Step {{ currentStep }} of {{ totalSteps }}</span>
      <span class="text-sm font-semibold text-gray-800">
        {{ currentStep === 1 ? 'Secure Your Account' : 'Personal Information' }}
      </span>
    </div>
    <p-progressBar [value]="getProgressPercentage()" [showValue]="false" styleClass="h-11"></p-progressBar>
  </div>

  <!-- Step 1: Password Setup -->
  @if (currentStep === 1) {
  <div class="space-y-3">
    <p-card styleClass="bg-white rounded-xl shadow-lg border-0 account-setup-card">
      <ng-template pTemplate="header">
        <div class="bg-primary rounded-t-xl p-4">
          <div class="flex items-center gap-3 text-xl font-medium">
            <i class="pi pi-lock text-2xl"></i>
            <h3 i18n>Create a Secure Password</h3>
          </div>
        </div>
      </ng-template>

      <div class="p-4">
        <form [formGroup]="passwordForm" (ngSubmit)="onPasswordSubmit()" class="space-y-6">
          <div class="grid grid-cols-1 gap-6">
            <div class="flex flex-col gap-2 ">
              <label for="password" class="font-medium text-gray-800 mb-1">
                Password <span class="text-red-500">*</span>
              </label>
              <p-password fluid
                id="password"
                formControlName="password"
                placeholder="Enter your password" i18n-placeholder
                class="w-full"
                [class.ng-invalid]="passwordForm.get('password')?.invalid && passwordSubmitAttempted"
                [toggleMask]="true"
                [feedback]="false"
              >
                <ng-template pTemplate="header">
                  <i class="pi pi-lock text-gray-500"></i>
                </ng-template>
              </p-password>
              <!-- <small class="text-red-500 text-sm mt-1" *ngIf="getPasswordError()">
                {{ getPasswordError() }}
              </small> -->
              <small class="text-gray-500 text-sm mt-1" i18n>Password must be at least 8 characters long, contain at least one lowercase letter, one uppercase letter, one digit, and one special character (e.g., @, #, $, _, etc.).</small>

              <!-- Password Strength Indicator -->
              <div class="mt-2" *ngIf="passwordForm.get('password')?.value">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-xs text-gray-600" i18n>Strength:</span>
                  <span class="text-xs font-medium" [class]="getCurrentPasswordStrength().color === 'bg-red-500' ? 'text-red-600' : getCurrentPasswordStrength().color === 'bg-yellow-500' ? 'text-yellow-600' : getCurrentPasswordStrength().color === 'bg-blue-500' ? 'text-blue-600' : 'text-green-600'">
                    {{ getCurrentPasswordStrength().label }}
                  </span>
                </div>
                <div class="flex gap-1">
                  <div *ngFor="let i of [1,2,3,4,5]" class="h-1 flex-1 rounded transition-all duration-200" [class]="i <= getCurrentPasswordStrength().score ? getCurrentPasswordStrength().color : 'bg-gray-200'">
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <label for="confirmPassword" class="font-medium text-gray-800 mb-1">
                Confirm Password <span class="text-red-500">*</span>
              </label>
              <p-password fluid
                id="confirmPassword"
                formControlName="confirmPassword"
                placeholder="Confirm your password" i18n-placeholder
                class="w-full "
                [class.ng-invalid]="passwordForm.get('confirmPassword')?.invalid && passwordSubmitAttempted"
                [toggleMask]="true"
                [feedback]="false"
              >
                <ng-template pTemplate="header">
                  <i class="pi pi-lock text-gray-500"></i>
                </ng-template>
              </p-password>
              <small class="text-red-500 text-sm mt-1" *ngIf="getConfirmPasswordError()">
                {{ getConfirmPasswordError() }}
              </small>
            </div>
          </div>

          <div class="flex justify-end pt-4">
            <p-button 
              type="submit" 
              label="Next Step" i18n-label
              [loading]="submitting"
              [disabled]="passwordForm.invalid || submitting"
              styleClass="p-button-primary"
            >
            </p-button>
          </div>
        </form>
      </div>
    </p-card>
  </div>
}

@if (currentStep === 2) {
  <!-- Step 2: Personal Information -->
  <div class="space-y-6">
    <form [formGroup]="personalInfoForm" (ngSubmit)="onPersonalInfoSubmit()" class="space-y-8">
        <p-card styleClass="p-4">
          <!-- Personal Information Section -->
          <div class="">
            <div class="flex items-center gap-3 mb-6">
              <i class="pi pi-user text-xl text-gray-600"></i>
              <h3 i18n>Personal Information</h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="flex flex-col gap-2">
                <label for="title" class="font-medium text-gray-800 mb-1">
                  Title (Optional)
                </label>
                <p-select 
                  id="title"
                  formControlName="title"
                  [options]="titles"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Mr." i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                >
                </p-select>
              </div>

              <div class="flex flex-col gap-2">
                <label for="firstName"  class="font-medium text-gray-800 mb-1">
                  First Name <span class="text-red-500">*</span>
                </label>
                <input 
                  id="firstName"
                  type="text" 
                  pInputText 
                  formControlName="firstName"
                  placeholder="John" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('firstName')"
                />
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'firstName')">
                  {{ getFieldError(personalInfoForm, 'firstName') }}
                </small>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div class="flex flex-col gap-2">
                <label for="lastName" class="font-medium text-gray-800 mb-1">
                  Last Name <span class="text-red-500">*</span>
                </label>
                <input 
                  id="lastName"
                  type="text" 
                  pInputText 
                  formControlName="lastName"
                  placeholder="Smith" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('lastName')"
                />
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'lastName')">
                  {{ getFieldError(personalInfoForm, 'lastName') }}
                </small>
              </div>

              <div class="flex flex-col gap-2">
                <label for="gender" class="font-medium text-gray-800 mb-1">
                  Gender <span class="text-red-500">*</span>
                </label>
                <p-select 
                  id="gender"
                  formControlName="gender"
                  [options]="genders"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select gender" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('gender')"
                >
                </p-select>
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'gender')">
                  {{ getFieldError(personalInfoForm, 'gender') }}
                </small>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div class="flex flex-col gap-2">
                <label for="mobileNumber" class="font-medium text-gray-800 mb-1">
                  Telephone <span class="text-red-500">*</span>
                </label>
                <input 
                  id="mobileNumber"
                  type="tel" 
                  pInputText 
                  formControlName="mobileNumber"
                  placeholder="+4477009000" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('mobileNumber')"
                />
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'mobileNumber')">
                  {{ getFieldError(personalInfoForm, 'mobileNumber') }}
                </small>
              </div>
            </div>
          </div>
        </p-card>
        <p-card styleClass="p-4">
          <!-- Residential Address Section -->
          <div class="">
            <div class="flex items-center gap-3 mb-6">
              <i class="pi pi-home text-xl text-gray-600"></i>
              <h3 class="">Residential Address</h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="flex flex-col gap-2">
                <label for="streetAddress" class="font-medium text-gray-800 mb-1">
                  Street Address <span class="text-red-500">*</span>
                </label>
                <input 
                  id="streetAddress"
                  type="text" 
                  pInputText 
                  formControlName="streetAddress"
                  placeholder="123 Main Street" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('streetAddress')"
                />
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'streetAddress')">
                  {{ getFieldError(personalInfoForm, 'streetAddress') }}
                </small>
              </div>

              <div class="flex flex-col gap-2">
                <label for="houseNumber" class="font-medium text-gray-800 mb-1">
                  House Number <span class="text-red-500">*</span>
                </label>
                <input 
                  id="houseNumber"
                  type="text" 
                  pInputText 
                  formControlName="houseNumber"
                  placeholder="123A" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('houseNumber')"
                />
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'houseNumber')">
                  {{ getFieldError(personalInfoForm, 'houseNumber') }}
                </small>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div class="flex flex-col gap-2">
                <label for="postcode" class="font-medium text-gray-800 mb-1">
                  Postcode <span class="text-red-500">*</span>
                </label>
                <input 
                  id="postcode"
                  type="text" 
                  pInputText 
                  formControlName="postcode"
                  placeholder="SW1A 1AA" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('postcode')"
                />
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'postcode')">
                  {{ getFieldError(personalInfoForm, 'postcode') }}
                </small>
              </div>

              <div class="flex flex-col gap-2">
                <label for="city" class="font-medium text-gray-800 mb-1">
                  City <span class="text-red-500">*</span>
                </label>
                <input 
                  id="city"
                  type="text" 
                  pInputText 
                  formControlName="city"
                  placeholder="London" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('city')"
                />
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'city')">
                  {{ getFieldError(personalInfoForm, 'city') }}
                </small>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-6 mt-6">
              <div class="flex flex-col gap-2">
                <label for="country" class="font-medium text-gray-800 mb-1">
                  Country <span class="text-red-500">*</span>
                </label>
                <p-select 
                  id="country"
                  formControlName="country"
                  [options]="countries"
                  optionLabel="name"
                  optionValue="name"
                  [filter]="true"
                  filterBy="name"
                  [showClear]="true"
                  placeholder="Select a Country" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('country')"
                >
                  <ng-template #selectedItem let-selectedOption>
                    <div class="flex items-center gap-2">
                      <div>{{ selectedOption.name }}</div>
                    </div>
                  </ng-template>
                  <ng-template let-country #item>
                    <div class="flex items-center gap-2">
                      <div>{{ country.name }}</div>
                    </div>
                  </ng-template>
                </p-select>
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'country')">
                  {{ getFieldError(personalInfoForm, 'country') }}
                </small>
              </div>
            </div>
          </div>
        </p-card>
        <p-card styleClass="p-4">
          <!-- Identification & Business Section -->
          <div class="">
            <div class="flex items-center gap-3 mb-6">
              <i class="pi pi-id-card text-xl text-gray-600"></i>
              <h3 class="">Identification & Business</h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="flex flex-col gap-2">
                <label for="citizenship" class="font-medium text-gray-800 mb-1">
                  Citizenship <span class="text-red-500">*</span>
                </label>
                <p-select 
                  id="citizenship"
                  formControlName="citizenship"
                  [options]="citizenships"
                  optionLabel="name"
                  optionValue="name"
                  [filter]="true"
                  filterBy="name"
                  [showClear]="true"
                  placeholder="Select a Citizenship" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('citizenship')"
                >
                  <ng-template #selectedItem let-selectedOption>
                    <div class="flex items-center gap-2">
                      <div>{{ selectedOption.name }}</div>
                    </div>
                  </ng-template>
                  <ng-template let-citizenship #item>
                    <div class="flex items-center gap-2">
                      <div>{{ citizenship.name }}</div>
                    </div>
                  </ng-template>
                </p-select>
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'citizenship')">
                  {{ getFieldError(personalInfoForm, 'citizenship') }}
                </small>
              </div>

              <div class="flex flex-col gap-2">
                <label for="nationalInsuranceNo" class="font-medium text-gray-800 mb-1">
                  National Insurance No. <span class="text-red-500">*</span>
                </label>
                <input 
                  id="nationalInsuranceNo"
                  type="text" 
                  pInputText 
                  formControlName="nationalInsuranceNo"
                  placeholder="12 34 56 C" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('nationalInsuranceNo')"
                />
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'nationalInsuranceNo')">
                  {{ getFieldError(personalInfoForm, 'nationalInsuranceNo') }}
                </small>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div class="flex flex-col gap-2">
                <label for="taxNumber" class="font-medium text-gray-800 mb-1">
                  Tax Number (Optional)
                </label>
                <input 
                  id="taxNumber"
                  type="text" 
                  pInputText 
                  formControlName="taxNumber"
                  placeholder="1234567890" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                />
              </div>

              <div class="flex flex-col gap-2">
                <label for="tradeLicence" class="font-medium text-gray-800 mb-1">
                  Trade Licence <span class="text-red-500">*</span>
                </label>
                <p-select 
                  id="tradeLicence"
                  formControlName="tradeLicence"
                  [options]="tradeLicences"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select option" i18n-placeholder
                  class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100"
                  [class.ng-invalid]="isFieldInvalid('tradeLicence')"
                >
                </p-select>
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'tradeLicence')">
                  {{ getFieldError(personalInfoForm, 'tradeLicence') }}
                </small>
              </div>
            </div>
          </div>
        </p-card>
        <p-card styleClass="p-4">
          <!-- ID Card Upload Section -->
          <div class="">
            <div class="flex items-center gap-3 mb-6">
              <i class="pi pi-cloud-upload text-xl text-gray-600"></i>
              <h3 class="">ID Card Upload <span class="text-red-500">*</span></h3>
            </div>

            <div class="grid grid-cols-1 gap-6">
              <div class="flex flex-col gap-2">
                <!-- File Upload Progress -->
                <div *ngIf="isFileUploading" class="mb-4">
                  <p-progressBar mode="indeterminate" styleClass="h-2"></p-progressBar>
                  <small class="text-gray-500 text-sm mt-2 block">Uploading file...</small>
                </div>

                <!-- Uploaded File Display -->
                <div *ngIf="uploadedFileUrl && !isFileUploading" class="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <i class="pi pi-file text-green-600"></i>
                      <span class="text-green-800 font-medium">File uploaded successfully</span>
                    </div>
                    <p-button appGlobalLoadingDisable 
                      type="button" 
                      icon="pi pi-times" 
                      severity="secondary" 
                      size="small"
                      (onClick)="removeUploadedFile()"
                      styleClass="p-button-text"
                    ></p-button>
                  </div>
                  
                  <!-- Show thumbnail only for image files -->
                  <div *ngIf="uploadedFile && isImageFile(uploadedFile)" class="mt-4">
                    <h5 class="text-sm font-medium text-gray-700 mb-2">Preview</h5>
                    <div class="flex justify-center">
                      <img [src]="uploadedFileUrl" alt="File preview" i18n-alt class="max-w-full h-auto max-h-48 object-contain border rounded-lg shadow-sm" />
                    </div>
                  </div>
                </div>

                <!-- Main File Upload with Drag & Drop -->
                <div 
                  class="mb-4 p-4 border-2 border-dashed rounded-lg text-center drag-drop-area"
                  [class]="dragOver ? 'border-blue-500 bg-blue-50 drag-over' : isFieldInvalid('idCard') ? 'border-red-500 bg-red-50' : 'border-gray-300'"
                  [class.loading]="isFileUploading"
                  (dragover)="onDragOver($event)"
                  (dragleave)="onDragLeave($event)"
                  (drop)="onDrop($event)"
                 >
                  
                  <input 
                    type="file" 
                    accept="application/pdf,image/png,image/jpeg,image/jpg"
                    (change)="onFileUpload($event)"
                    class="hidden"
                    [disabled]="isFileUploading"
                    id="file-input-idCard" />
                  
                  <div class="flex flex-col items-center justify-center py-4">
                    <svg class="mb-2" width="38" height="27" viewBox="0 0 38 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.6875 26.875C4.0293 26.875 0.25 23.0957 0.25 18.4375C0.25 14.7578 2.60547 11.6289 5.88672 10.4746C5.88086 10.3164 5.875 10.1582 5.875 10C5.875 4.82031 10.0703 0.625 15.25 0.625C18.7246 0.625 21.7539 2.51172 23.377 5.32422C24.2676 4.72656 25.3457 4.375 26.5 4.375C29.6055 4.375 32.125 6.89453 32.125 10C32.125 10.7148 31.9902 11.3945 31.75 12.0273C35.1719 12.7188 37.75 15.748 37.75 19.375C37.75 23.5176 34.3926 26.875 30.25 26.875H8.6875ZM13.3164 14.1602C12.7656 14.7109 12.7656 15.6016 13.3164 16.1465C13.8672 16.6914 14.7578 16.6973 15.3027 16.1465L17.5879 13.8613V21.7188C17.5879 22.498 18.2148 23.125 18.9941 23.125C19.7734 23.125 20.4004 22.498 20.4004 21.7188V13.8613L22.6855 16.1465C23.2363 16.6973 24.127 16.6973 24.6719 16.1465C25.2168 15.5957 25.2227 14.7051 24.6719 14.1602L19.9844 9.47266C19.4336 8.92188 18.543 8.92188 17.998 9.47266L13.3105 14.1602H13.3164Z" fill="#9CA3AF"/>
                    </svg>
                    <label 
                      for="file-input-idCard" 
                      class="cursor-pointer text-blue-600 hover:text-blue-800 font-medium"
                      [class.opacity-50]="isFileUploading"
                      [class.cursor-not-allowed]="isFileUploading">
                      {{ isFileUploading ? 'Uploading...' : 'Click to upload or drag and drop' }}
                    </label>
                    <p class="text-sm text-gray-500 mt-2">
                      PDF, PNG, JPG (MAX. 10MB)
                    </p>
                  </div>
                </div>
                <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError(personalInfoForm, 'idCard')">
                  {{ getFieldError(personalInfoForm, 'idCard') }}
                </small>
              </div>
            </div>
          </div>
        </p-card>
          <!-- Missing Fields Summary (Mobile-friendly, shown near submit button) -->
          <div *ngIf="personalInfoSubmitAttempted && personalInfoForm.invalid" 
               class="error-summary-container mb-4 p-4 bg-red-50 border border-red-200 rounded-lg shadow-md">
            <div class="flex items-start gap-2 mb-2">
              <i class="pi pi-exclamation-triangle text-red-600 mt-0.5"></i>
              <div class="flex-1">
                <h4 class="text-red-800 font-semibold mb-2 text-base" i18n>Please complete the following required fields:</h4>
                <ul class="list-disc list-inside space-y-1 text-red-700 text-sm">
                  <li *ngFor="let field of getMissingFields()">{{ field }}</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end pt-6">
            <p-button 
              type="submit" 
              label="Submit Application" i18n-label
              [loading]="submitting"
              [disabled]="submitting"
              styleClass="p-button-primary"
            >
            </p-button>
          </div>
        

  </form>
  </div>
}
  <!-- Loading Spinner -->
  <div class="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50" *ngIf="loading || redirecting">
    <div class="text-center bg-white p-8 rounded-xl shadow-lg">
      <i class="pi pi-spin pi-spinner text-indigo-500 mb-4 text-3xl"></i>
      <p class="m-0 text-gray-600 font-medium" i18n>{{ redirecting ? 'Redirecting to payment...' : 'Loading...' }}</p>
    </div>
  </div>

  <!-- Terms and Conditions Modal -->
  <app-terms-conditions-modal
    [(visible)]="showTermsModal"
    (accepted)="onTermsAccepted()"
    (visibleChange)="onTermsModalVisibleChange($event)">
  </app-terms-conditions-modal>
</div>
